/**
 * Strategies and tactics nested under one objective, for Box 7 of the ICS-201.
 *
 * The export has always printed these three levels together, but before escalation there was nowhere to enter the
 * lower two: the Work Analysis Matrix that edits them belongs to the Planning P cycle. So the box printed objectives
 * and actions and left the strategy and tactic lines blank.
 *
 * The nesting matches the 234's: a strategy belongs to objectives, a tactic belongs to strategies, and a tactic's text
 * lives in `description` rather than `name`.
 */

import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import PratusTextArea from 'components/DesignSystems/New/PratusTextArea';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';

import { INCIDENT } from '../ICSConstants';
import {
  useCreateIncidentStrategy,
  useEditIncidentStrategy,
  useIncidentStrategies,
  type IncidentStrategy,
} from '../hooks/incidentStrategyHooks';
import {
  useCreateIncidentTactic,
  useEditIncidentTactic,
  useIncidentTactics,
  type IncidentTactic,
} from '../hooks/incidentTacticHooks';

const tacticText = (tactic: IncidentTactic): string =>
  (tactic.description || tactic.name || '').trim();

export const ObjectiveStrategiesTactics: React.FC<{
  objectiveId: string;
  incidentId?: string;
}> = ({ objectiveId, incidentId }) => {
  const { data: strategies } = useIncidentStrategies(INCIDENT);
  const { data: tactics } = useIncidentTactics(INCIDENT);

  const createStrategy = useCreateIncidentStrategy();
  const editStrategy = useEditIncidentStrategy();
  const createTactic = useCreateIncidentTactic();
  const editTactic = useEditIncidentTactic();

  const [editingStrategy, setEditingStrategy] = useState<string | null>(null);
  const [editingTactic, setEditingTactic] = useState<string | null>(null);

  const objectiveStrategies = useMemo(
    () =>
      (strategies ?? []).filter(
        (strategy) =>
          !strategy.archived && strategy.objectives.includes(objectiveId)
      ),
    [strategies, objectiveId]
  );

  const tacticsForStrategy = (strategyId: string): IncidentTactic[] =>
    (tactics ?? []).filter(
      (tactic) => !tactic.archived && tactic.strategies.includes(strategyId)
    );

  const handleAddStrategy = async () => {
    await createStrategy.mutateAsync({
      name: 'New Strategy',
      description: '',
      tactics: [],
      objectives: [objectiveId],
      incident_id: incidentId,
    });
  };

  const handleAddTactic = async (strategy: IncidentStrategy) => {
    await createTactic.mutateAsync({
      name: '',
      description: 'New Tactic',
      strategies: [strategy.strategy_id],
      incident_id: strategy.incident_id,
    });
  };

  return (
    <div className="objective-card">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span>Strategies &amp; Tactics</span>
        <StylishNewButton
          onClick={handleAddStrategy}
          className={classNames('button--secondary')}
        >
          <SharedIcon iconName="add" classes="me-2" />
          Add Strategy
        </StylishNewButton>
      </div>

      {objectiveStrategies.length === 0 ? (
        <div className="text-muted ms-2 mb-2">
          No strategies yet. These print in Box 7 of the ICS-201.
        </div>
      ) : null}

      {objectiveStrategies.map((strategy) => (
        <div key={strategy.strategy_id} className="mb-3 ms-2">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div className="flex-grow-1">
              {editingStrategy === strategy.strategy_id ? (
                <PratusTextArea
                  defaultValue={strategy.name}
                  maxLength={200}
                  minLength={1}
                  onSave={async (value: string) => {
                    await editStrategy.mutateAsync({ ...strategy, name: value });
                    setEditingStrategy(null);
                  }}
                  onCancel={() => setEditingStrategy(null)}
                />
              ) : (
                <span className="item-name">{strategy.name}</span>
              )}
            </div>
            <div className="d-flex align-items-start flex-shrink-0 flex-row gap-1">
              <StylishNewButton
                className="button--link action-button"
                onClick={() => setEditingStrategy(strategy.strategy_id)}
              >
                <SharedIcon iconName="edit" size="sm" />
              </StylishNewButton>
              <StylishNewButton
                className="button--link action-button"
                onClick={() =>
                  editStrategy.mutateAsync({ ...strategy, archived: true })
                }
              >
                <SharedIcon iconName="delete" size="sm" />
              </StylishNewButton>
              <StylishNewButton
                className="button--link action-button"
                onClick={() => handleAddTactic(strategy)}
              >
                <SharedIcon iconName="add" size="sm" />
              </StylishNewButton>
            </div>
          </div>

          <div className="ms-4">
            {tacticsForStrategy(strategy.strategy_id).map((tactic) => (
              <div
                key={tactic.tactic_id}
                className="d-flex align-items-start justify-content-between gap-2"
              >
                <div className="flex-grow-1">
                  {editingTactic === tactic.tactic_id ? (
                    <PratusTextArea
                      defaultValue={tacticText(tactic)}
                      maxLength={200}
                      minLength={1}
                      onSave={async (value: string) => {
                        await editTactic.mutateAsync({
                          ...tactic,
                          description: value,
                        });
                        setEditingTactic(null);
                      }}
                      onCancel={() => setEditingTactic(null)}
                    />
                  ) : (
                    <span className="item-name">{tacticText(tactic)}</span>
                  )}
                </div>
                <div className="d-flex align-items-start flex-shrink-0 flex-row gap-1">
                  <StylishNewButton
                    className="button--link action-button"
                    onClick={() => setEditingTactic(tactic.tactic_id)}
                  >
                    <SharedIcon iconName="edit" size="sm" />
                  </StylishNewButton>
                  <StylishNewButton
                    className="button--link action-button"
                    onClick={() =>
                      editTactic.mutateAsync({ ...tactic, archived: true })
                    }
                  >
                    <SharedIcon iconName="delete" size="sm" />
                  </StylishNewButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ObjectiveStrategiesTactics;
